import { defineStore, storeToRefs } from 'pinia'
import type { GetPermissionRequest } from '@/models/api/get-permission-request'
import { api } from '@/services'
import { useUserStore } from '@/stores/use-user-store'
import { isLoading } from '@/utils/functions/is-loading'
import { useEnv } from '~library/composables'
import { Identity } from '~library/enums'
import { isApiSuccess } from '~library/utils/functions'
import { useIdentityStore } from './use-identity-store'

/** 企業官網購票權限管理 */
export const useBookFlightPermissionStore = defineStore(
  'bookFlightPermission',
  () => {
    const { VITE_ENABLE_G5 } = useEnv()
    const { isCorporateAccount, corporateAccountData } =
      storeToRefs(useUserStore())

    const { identity } = storeToRefs(useIdentityStore())

    /** 企業官網購票權限，null 表示尚未取得 */
    const bookingPermission = ref<boolean | null>(null)

    /** 是否顯示查詢航班及訂票功能 (TODO: 未來移除 VITE_ENABLE_G5 檢查) */
    const showBookFlight = computed(
      () => VITE_ENABLE_G5 && bookingPermission.value
    )

    /**
     * 判斷是否為企業員工身份
     */
    function isCompanyEmployeeIdentity(
      identity?: Identity
    ): identity is GetPermissionRequest['identity'] {
      if (!identity) return false

      return [
        Identity.TRAVELER,
        Identity.MAIN_MANAGER,
        Identity.DEPUTY_MANAGER,
        Identity.AGENT,
        Identity.MICE
      ].includes(identity)
    }

    /**
     * 取得 booking 權限
     */
    async function fetchBookingPermission() {
      if (!identity.value) {
        bookingPermission.value = false
        return null
      }

      // 企業帳號:從 corporateAccountData 取得
      if (isCorporateAccount.value) {
        bookingPermission.value = corporateAccountData.value?.booking ?? null
        return
      }

      // 企業員工:呼叫 API 取得權限
      if (isCompanyEmployeeIdentity(identity.value)) {
        try {
          const responseData = await isLoading(
            api.companyEmployee.getPermission({
              identity: identity.value
            }),
            'spinner',
            false
          )

          if (isApiSuccess(responseData)) {
            bookingPermission.value = responseData.data.booking
          } else {
            bookingPermission.value = false
          }
        } catch (error) {
          console.error('Failed to fetch booking permission:', error)
          bookingPermission.value = false
        }
        return
      }

      // 其他情況(訪客、未登入等):無權限
      bookingPermission.value = false
    }

    /**
     * 清除權限資料
     */
    function clearPermissions() {
      bookingPermission.value = false
    }

    // 監聽 userProfile.identity 變化,自動更新權限
    watch(
      () => identity.value,
      async (newIdentity, oldIdentity) => {
        if (newIdentity === oldIdentity) {
          return
        }

        await fetchBookingPermission()
      },
      { immediate: true }
    )

    // 監聽企業帳號資料變化,更新權限
    watch(
      () => corporateAccountData.value?.booking,
      (newBooking) => {
        if (isCorporateAccount.value) {
          bookingPermission.value = newBooking ?? false
        }
      }
    )

    return {
      /** 企業官網購票權限，null 表示尚未取得 */
      bookingPermission,
      /** 是否顯示查詢航班及訂票功能 (TODO: 未來移除 VITE_ENABLE_G5 檢查) */
      showBookFlight,
      /** 取得 booking 權限 */
      fetchBookingPermission,
      /** 清除權限資料 */
      clearPermissions
    }
  }
)
